import { Button } from '@modusign/design-system';
import PropTypes from 'prop-types';
import PropsType from 'props-type';
import { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';

import useBulkRequestItem from '@app/features/bulkRequest/hooks/useBulkRequestItem';
import useUpdateBulkRequestSigningDuration from '@app/features/bulkRequest/hooks/useUpdateBulkRequestSigningDuration';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { modalClose } from '@app/store/modal/actions';

import './BulkRequestSigningExpiryPeriodChangeModal.scss';

const propTypes = {
  bulkRequestId: PropTypes.string.isRequired,
};

type BulkRequestSigningExpiryPeriodChangeModalProps = PropsType<typeof propTypes>;

function minutesToDays(minutes: number) {
  return Math.trunc(minutes / 60 / 24);
}

function daysToMinutes(days: number) {
  return days * 24 * 60;
}

function BulkRequestSigningExpiryPeriodChangeModal({
  bulkRequestId,
}: BulkRequestSigningExpiryPeriodChangeModalProps) {
  const dispatch = useDispatch();

  const { data } = useBulkRequestItem(bulkRequestId);

  const initialSigningExpiryPeriodDay = minutesToDays(data?.signingDuration ?? 0);
  const [signingExpiryPeriodDay, setSigningExpiryPeriodDay] = useState(
    initialSigningExpiryPeriodDay
  );

  const { createErrorHandler } = useOpenErrorModal();
  const workspaceId = useWorkspaceId();
  const { mutateAsync: updateBulkRequestSigningDuration, isLoading } =
    useUpdateBulkRequestSigningDuration();

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setSigningExpiryPeriodDay(Math.abs(parseInt(value, 10)));
  }

  return (
    <div className="Container Container--max-xxs">
      <div className="BulkRequestSigningExpiryPeriodChangeModal__card Card Card--pop align-center">
        <h3 className="space-6x">
          <strong>서명 유효기간 변경</strong>
        </h3>

        <div className="BulkRequestSigningExpiryPeriodChangeModal__form Card Card--theme-flat Card--primary space-6x">
          <p>각 서명자는 요청 메시지 수신 후,</p>
          <div>
            <input
              className="BulkRequestSigningExpiryPeriodChangeModal__input"
              max={365}
              type="number"
              value={signingExpiryPeriodDay}
              onBlur={handleChange}
              onChange={handleChange}
            />
            일 내에 서명을 완료해야합니다.
          </div>
        </div>

        <button
          className="Btn"
          type="button"
          onClick={() => {
            dispatch(modalClose());
          }}
        >
          닫기
        </button>
        <Button
          disabled={
            signingExpiryPeriodDay > 0 && signingExpiryPeriodDay === initialSigningExpiryPeriodDay
          }
          isLoading={isLoading}
          variant="primary"
          onClick={() => {
            updateBulkRequestSigningDuration(
              [
                workspaceId,
                bulkRequestId,
                {
                  signingDuration: daysToMinutes(signingExpiryPeriodDay),
                },
              ],
              {
                onSuccess: () => {
                  dispatch(modalClose());
                },
                onError: createErrorHandler({ title: '대량전송 서명 유효기간 변경 실패' }),
              }
            );
          }}
        >
          변경하기
        </Button>
      </div>
    </div>
  );
}

BulkRequestSigningExpiryPeriodChangeModal.propTypes = propTypes;

export default BulkRequestSigningExpiryPeriodChangeModal;
